import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import {
  systemAuth, clearSystemTokens, storeSystemTokens,
  setSystemSessionEndedHandler, getSystemAccessToken,
  type SystemAdmin,
} from './system.api';

/**
 * The console's session - entirely separate from AuthContext.
 *
 * NOT a variant of the tenant context, and the two are never nested. An
 * operator has no organisation, no role and no department, so there is
 * nothing for tenant code to read off this; keeping them apart means a
 * component cannot accidentally be written against "whichever session
 * happens to be there".
 */

interface SystemAuthValue {
  admin: SystemAdmin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const SystemAuthContext = createContext<SystemAuthValue | null>(null);

export function SystemAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<SystemAdmin | null>(null);
  const [isLoading, setLoading] = useState(true);

  const signOut = useCallback(() => {
    clearSystemTokens();
    setAdmin(null);
  }, []);

  /*
   * A 401 on any console call ends the session here. The API ends sessions
   * deliberately - a deactivation, a password change - so the right
   * response is to ask for the password again, not to paper over it.
   */
  useEffect(() => {
    setSystemSessionEndedHandler(() => setAdmin(null));
    return () => setSystemSessionEndedHandler(null);
  }, []);

  /*
   * Revive a session across a page reload.
   *
   * The stored access token is tried first via /auth/me. If it has expired
   * the refresh token is spent ONCE - not on every 401, which is why the
   * client has no refresh interceptor. Any failure clears both tokens and
   * shows the login screen; a console that limps along on a half-valid
   * session is worse than one that asks again.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (getSystemAccessToken()) {
          const me = await systemAuth.me();
          if (!cancelled) setAdmin(me);
          return;
        }
        const refreshToken = systemAuth.getStoredRefreshToken();
        if (refreshToken) {
          const tokens = await systemAuth.refresh(refreshToken);
          storeSystemTokens(tokens);
          const me = await systemAuth.me();
          if (!cancelled) setAdmin(me);
        }
      } catch {
        clearSystemTokens();
        if (!cancelled) setAdmin(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await systemAuth.login(email, password);
    storeSystemTokens(result.tokens);
    setAdmin(result.admin);
  }, []);

  const value = useMemo<SystemAuthValue>(
    () => ({ admin, isAuthenticated: admin !== null, isLoading, signIn, signOut }),
    [admin, isLoading, signIn, signOut],
  );

  return <SystemAuthContext.Provider value={value}>{children}</SystemAuthContext.Provider>;
}

export function useSystemAuth(): SystemAuthValue {
  const value = useContext(SystemAuthContext);
  if (!value) throw new Error('useSystemAuth must be used inside SystemAuthProvider');
  return value;
}
